import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { TaskLoaderMark } from '../../chat/TaskLoaderMark';

// Shown while a turn is in flight but there's no thinking step to surface yet (the
// gap before the first step, or a tool with no thinkingMessage): the looping Orion
// task mark with a generic "Thinking…" label. Shares ThinkingLine's layout + shimmer
// so the swap to the first real step is seamless. The label agrees with the assistant's
// gender — Hebrew "חושב" (m) vs "חושבת" (f); only he/hi actually differ (chat.thinking.female).
export function ThinkingLoaderOnly() {
  const { t } = useTranslation();
  const { gender } = useAgentName();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, paddingInlineStart: 2 }}>
      <TaskLoaderMark />
      <span
        className="orion-think-line"
        style={{ fontSize: 14, lineHeight: 1.5, fontWeight: 'var(--sym-weight-medium)', letterSpacing: '0.01em' }}
      >
        {t(gender === 'female' ? 'chat.thinking.female' : 'chat.thinking')}
      </span>
    </div>
  );
}

// The live "what Orion is doing right now" line — the latest THINKING step of the
// in-flight turn, one at a time, with a left-to-right shimmer so it reads as active.
export function ThinkingLine({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, paddingInlineStart: 2 }}>
      <TaskLoaderMark />
      <span
        key={text}
        className="orion-think-line"
        style={{ fontSize: 14, lineHeight: 1.5, fontWeight: 'var(--sym-weight-medium)', letterSpacing: '0.01em' }}
      >
        {text}
      </span>
    </div>
  );
}

// A thread-shaped shimmer skeleton — the loading state echoes what's about to
// arrive (a user bubble on the right, assistant prose lines on the left).
export function ConversationLoader({ floating }: { floating: boolean }) {
  const userBubble = (width: string) => (
    <div className="orion-shimmer" style={{ alignSelf: 'flex-end', width, height: 44, borderRadius: 'var(--sym-radius-chat-bubble)' }} />
  );
  const assistantLines = (widths: string[]) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {widths.map((w, i) => (
        <div key={i} className="orion-shimmer" style={{ width: w, height: 13, borderRadius: 6 }} />
      ))}
    </div>
  );
  return (
    <div
      aria-hidden
      style={{
        minHeight: floating ? 220 : undefined,
        width: '100%',
        maxWidth: 720,
        margin: '0 auto',
        padding: floating ? '20px 4px' : '28px 4px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 26,
        pointerEvents: 'none',
      }}
    >
      {userBubble('42%')}
      {assistantLines(['88%', '76%', '48%'])}
      {!floating && userBubble('28%')}
      {!floating && assistantLines(['70%', '40%'])}
    </div>
  );
}

export function useTypewriter(text: string, skipInitial: boolean) {
  const [charIndex, setCharIndex] = useState(skipInitial ? text.length : 0);
  const textRef = useRef(text);

  useEffect(() => {
    textRef.current = text;
  });

  useEffect(() => {
    if (skipInitial) return;
    // One tick per frame, not three. A 10ms interval fired ~100×/s on a 60Hz display, so two of every
    // three ticks re-rendered this row for a paint that never happened. The STEP is scaled by the same
    // factor as the interval, so the reveal speed is unchanged (200 chars/s while nearly caught up,
    // 500 when far behind the stream) — raising the interval alone would slow it 3.3× and let the
    // typewriter fall behind the SSE feed, leaving the cursor running after the turn ended.
    const id = setInterval(() => {
      setCharIndex((i) => {
        const len = textRef.current.length;
        if (i >= len) return i;
        const gap = len - i;
        const step = Math.min(17, Math.max(7, Math.ceil(gap / 9)));
        return Math.min(i + step, len);
      });
    }, 33);
    return () => clearInterval(id);
  }, [skipInitial]);

  return text.slice(0, charIndex);
}
